<template>
  <div class="hris-page">
    <div class="hris-page-inner">
    <div class="hris-page-header">
      <span class="hris-page-eyebrow">Employee Records</span>
      <h1 class="hris-page-title">Incidents &amp; Sanctions</h1>
      <p class="hris-page-subtitle">Incident reports, notices to explain, and your sanctions.</p>
    </div>

    <div class="hris-panel" style="padding: 0; margin-bottom: 20px; overflow: hidden;">
      <q-tabs
        v-model="mainTab"
        active-color="green"
        indicator-color="green"
        align="left"
        inline-label
        class="hris-sanctions-tabs"
        @input="onTabChange"
      >
        <q-tab name="incidents" label="Incident Reports" icon="fas fa-exclamation-triangle" ></q-tab>
        <q-tab name="ntes" label="Pending Explanations" icon="fas fa-file-invoice" ></q-tab>
        <q-tab name="sanctions" label="My Sanctions" icon="fas fa-gavel" ></q-tab>
      </q-tabs>
    </div>

    <q-tab-panels v-model="mainTab" animated style="background: transparent" class="q-pa-none">
      <q-tab-panel name="incidents" class="q-pa-none">
      <div class="row" v-if="mainTab === 'incidents'">
        <div class="col-12">
          <div class="hris-toolbar">
            <q-btn
              unelevated
              class="hris-q-primary"
              icon="fas fa-plus"
              label="Create Incident Report"
              @click="openIncidentDialog"
            ></q-btn>
            <q-select
              v-model="incidentStatusFilter"
              :options="incidentStatusOptions"
              label="Filter Status"
              dense
              outlined
              color="green"
              emit-value
              map-options
              style="width: 200px"
              @input="getIncidentReports"
            ></q-select>
            <div class="hris-toolbar-spacer"></div>
            <q-input
              v-model="incidentFilter"
              placeholder="Search Incident Reports"
              dense
              outlined
              color="green"
              style="min-width: 220px;"
            ></q-input>
          </div>
        </div>

        <div class="col-12">
          <div class="hris-panel">
            <q-table
              flat
              :data="incidentReports"
              :columns="incidentColumns"
              row-key="id"
              :filter="incidentFilter"
              :loading="incidentLoading"
              separator="cell"
              class="my-sticky-column-table"
            >
              <template v-slot:loading>
                <div class="hris-loading absolute-full flex flex-center" style="background: rgba(255,255,255,0.7); z-index: 1;">
                  <q-spinner color="green" size="40px" ></q-spinner>
                  <div>Loading incident reports...</div>
                </div>
              </template>
              <template v-slot:no-data>
                <div class="hris-empty full-width">No incident reports found.</div>
              </template>
              <template v-slot:body="props">
                <q-tr :props="props">
                  <q-td key="employee_reported" :props="props">
                    <div>
                      {{
                        props.row.employee
                          ? props.row.employee.firstname +
                            " " +
                            props.row.employee.lastname
                          : "N/A"
                      }}
                    </div>
                    <div
                      v-if="props.row.reported_employee_id"
                      class="text-caption text-grey-7"
                    >
                      {{ props.row.reported_employee_id }}
                    </div>
                  </q-td>
                  <q-td key="incident_date" :props="props">{{
                    formatDate(props.row.incident_date)
                  }}</q-td>
                  <q-td
                    key="reason"
                    :props="props"
                    style="white-space: pre-wrap; max-width: 260px"
                    >{{ props.row.reason }}</q-td
                  >
                  <q-td key="status" :props="props">
                    <q-chip
                      :color="getIncidentStatusColor(props.row.status)"
                      text-color="white"
                      dense
                    >
                      {{ props.row.status }}
                    </q-chip>
                  </q-td>
                  <q-td key="issued_by" :props="props">
                    {{
                      props.row.issuer
                        ? props.row.issuer.firstname +
                          " " +
                          props.row.issuer.lastname
                        : "N/A"
                    }}
                  </q-td>
                  <q-td key="actions" :props="props">
                    <q-btn
                      flat
                      round
                      dense
                      class="hris-q-ghost"
                      icon="fas fa-eye"
                      @click="viewIncidentDetails(props.row)"
                    >
                      <q-tooltip>View Details</q-tooltip>
                    </q-btn>
                    <q-btn
                      flat
                      round
                      dense
                      class="hris-q-ghost"
                      icon="fas fa-edit"
                      @click="editIncidentReport(props.row)"
                      v-if="props.row.status === 'active'"
                    >
                      <q-tooltip>Edit</q-tooltip>
                    </q-btn>
                    <q-btn
                      flat
                      round
                      dense
                      class="hris-q-ghost"
                      icon="fas fa-check"
                      @click="confirmResolveIncident(props.row)"
                      v-if="props.row.status === 'active'"
                    >
                      <q-tooltip>Mark Resolved</q-tooltip>
                    </q-btn>
                  </q-td>
                </q-tr>
              </template>
            </q-table>
          </div>
        </div>
      </div>
      </q-tab-panel>

      <q-tab-panel name="ntes" class="q-pa-none">
      <div class="row" v-if="mainTab === 'ntes'">
        <div class="col-12">
          <div class="hris-toolbar">
            <q-btn
              unelevated
              class="hris-q-outline"
              icon="fas fa-sync"
              label="Refresh"
              @click="getMyNtes"
            />
            <div class="hris-toolbar-spacer"></div>
            <q-input
              v-model="nteFilter"
              placeholder="Search NTEs"
              dense
              outlined
              color="green"
              style="min-width: 220px;"
            />
          </div>
        </div>

        <div class="col-12">
          <div class="hris-panel">
            <q-table
              flat
              :data="ntes"
              :columns="nteColumns"
              row-key="id"
              :filter="nteFilter"
              :loading="nteLoading"
              separator="cell"
            >
              <template v-slot:loading>
                <div class="hris-loading absolute-full flex flex-center" style="background: rgba(255,255,255,0.7); z-index: 1;">
                  <q-spinner color="green" size="40px" />
                  <div>Loading notices...</div>
                </div>
              </template>
              <template v-slot:no-data>
                <div class="hris-empty full-width">No pending explanations found.</div>
              </template>
              <template v-slot:body="props">
                <q-tr :props="props">
                  <q-td key="incident_date" :props="props">{{
                    formatDate(props.row.incident_report ? props.row.incident_report.incident_date : null)
                  }}</q-td>
                  <q-td key="deadline" :props="props">{{
                    formatDate(props.row.deadline)
                  }}</q-td>
                  <q-td key="status" :props="props">
                    <q-chip
                      :color="getNteStatusColor(props.row.status)"
                      text-color="white"
                      dense
                    >
                      {{ props.row.status }}
                    </q-chip>
                  </q-td>
                  <q-td key="issued_by" :props="props">
                    {{
                      props.row.issuer
                        ? props.row.issuer.firstname +
                          " " +
                          props.row.issuer.lastname
                        : "N/A"
                    }}
                  </q-td>
                  <q-td key="actions" :props="props">
                    <q-btn
                      flat
                      round
                      dense
                      class="hris-q-ghost"
                      icon="fas fa-eye"
                      @click="viewNteDetails(props.row)"
                    >
                      <q-tooltip>View Details</q-tooltip>
                    </q-btn>
                    <q-btn
                      flat
                      round
                      dense
                      class="hris-q-ghost"
                      icon="fas fa-edit"
                      @click="openExplanationDialog(props.row)"
                      v-if="['pending', 'submitted'].includes(props.row.status)"
                    >
                      <q-tooltip>Submit Explanation</q-tooltip>
                    </q-btn>
                  </q-td>
                </q-tr>
              </template>
            </q-table>
          </div>
        </div>
      </div>
      </q-tab-panel>

      <q-tab-panel name="sanctions" class="q-pa-none">
      <div class="row" v-if="mainTab === 'sanctions'">
        <div class="col-12">
          <div class="hris-toolbar">
            <div class="hris-toolbar-spacer"></div>
            <q-input
              v-model="filter"
              placeholder="Search Sanctions"
              dense
              outlined
              color="green"
              style="width: 300px"
            />
          </div>
        </div>

        <div class="col-12">
          <div class="hris-panel">
            <q-table
              flat
              :data="sanctions"
              :columns="columns"
              row-key="id"
              :filter="filter"
              :loading="loading"
              separator="cell"
            >
              <template v-slot:loading>
                <div class="hris-loading absolute-full flex flex-center" style="background: rgba(255,255,255,0.7); z-index: 1;">
                  <q-spinner color="green" size="40px" />
                  <div>Loading sanctions...</div>
                </div>
              </template>
              <template v-slot:no-data>
                <div class="hris-empty full-width">No sanctions on record.</div>
              </template>
              <template v-slot:body="props">
                <q-tr :props="props">
                  <q-td key="sanction_type" :props="props">
                    <q-chip
                      :color="getSanctionColor(props.row.sanction_type)"
                      text-color="white"
                      dense
                    >
                      {{ props.row.sanction_type }}
                    </q-chip>
                  </q-td>
                  <q-td key="offense" :props="props">
                    {{ props.row.offense ? props.row.offense.name : 'N/A' }}
                  </q-td>
                  <q-td key="incident_date" :props="props">{{
                    formatDate(props.row.incident_date)
                  }}</q-td>
                  <q-td key="issued_date" :props="props">{{
                    formatDate(props.row.issued_date)
                  }}</q-td>
                  <q-td key="effective_date" :props="props">{{
                    formatDate(props.row.effective_date)
                  }}</q-td>
                  <q-td key="end_date" :props="props">{{
                    formatDate(props.row.end_date)
                  }}</q-td>
                  <q-td key="status" :props="props">
                    <q-chip
                      :color="props.row.status === 'active' ? 'green' : 'red'"
                      text-color="white"
                      dense
                    >
                      {{ props.row.status }}
                    </q-chip>
                  </q-td>
                  <q-td key="issued_by" :props="props">
                    {{
                      props.row.issuer
                        ? props.row.issuer.firstname +
                          " " +
                          props.row.issuer.lastname
                        : "N/A"
                    }}
                  </q-td>
                  <q-td key="actions" :props="props">
                    <q-btn
                      flat
                      round
                      dense
                      class="hris-q-ghost"
                      icon="fas fa-eye"
                      @click="viewDetails(props.row)"
                    >
                      <q-tooltip>View Details</q-tooltip>
                    </q-btn>
                  </q-td>
                </q-tr>
              </template>
            </q-table>
          </div>
        </div>
      </div>
      </q-tab-panel>
    </q-tab-panels>

    <!-- Submit Explanation Dialog -->
    <q-dialog v-model="explanationDialog" persistent>
      <div class="hris-modal">
        <div class="hris-modal-header">
          <h2 class="hris-modal-title">Submit Explanation</h2>
          <q-btn icon="fas fa-times" flat round dense v-close-popup class="hris-q-ghost" />
        </div>
        <div class="hris-modal-body">
          <q-form @submit="submitExplanation" class="q-gutter-md">
            <q-input
              v-model="explanationForm.explanation"
              label="Your Explanation"
              type="textarea"
              outlined
              dense
              color="green"
              :rules="[(val) => !!val || 'Explanation is required']"
              hint="Provide your response to the incident report."
            />
            <div class="hris-form-actions" style="margin-top: 8px; padding-top: 16px;">
              <q-btn label="Cancel" unelevated class="hris-q-ghost" v-close-popup />
              <q-btn
                label="Submit Explanation"
                type="submit"
                unelevated
                class="hris-q-primary"
                :loading="explanationSaving"
              />
            </div>
          </q-form>
        </div>
      </div>
    </q-dialog>

    <!-- NTE Details Dialog -->
    <q-dialog v-model="nteDetailsDialog">
      <div class="hris-modal">
        <div class="hris-modal-header">
          <h2 class="hris-modal-title">NTE Details</h2>
          <q-btn icon="fas fa-times" flat round dense v-close-popup class="hris-q-ghost" />
        </div>
        <div class="hris-modal-body" v-if="selectedNte">
          <div class="row q-mb-sm" v-if="selectedNte.incident_report">
            <div class="col-4 text-weight-bold">Incident Date:</div>
            <div class="col-8">{{ formatDate(selectedNte.incident_report.incident_date) }}</div>
          </div>
          <div class="row q-mb-sm" v-if="selectedNte.incident_report">
            <div class="col-4 text-weight-bold">Incident Reason:</div>
            <div class="col-8" style="white-space: pre-wrap">{{ selectedNte.incident_report.reason }}</div>
          </div>
          <q-separator class="q-my-md" />
          <div class="row q-mb-sm">
            <div class="col-4 text-weight-bold">Deadline:</div>
            <div class="col-8">{{ formatDate(selectedNte.deadline) }}</div>
          </div>
          <div class="row q-mb-sm">
            <div class="col-4 text-weight-bold">Status:</div>
            <div class="col-8">
              <q-chip :color="getNteStatusColor(selectedNte.status)" text-color="white" dense>
                {{ selectedNte.status }}
              </q-chip>
            </div>
          </div>
          <div class="row q-mb-sm">
            <div class="col-4 text-weight-bold">My Explanation:</div>
            <div class="col-8" style="white-space: pre-wrap">{{ selectedNte.explanation || 'Not submitted yet.' }}</div>
          </div>
          <div class="row q-mb-sm">
            <div class="col-4 text-weight-bold">Admin Resolution Notes:</div>
            <div class="col-8" style="white-space: pre-wrap">{{ selectedNte.admin_notes || 'N/A' }}</div>
          </div>
        </div>
      </div>
    </q-dialog>

    <!-- Details Dialog -->
    <q-dialog v-model="detailsDialog">
      <div class="hris-modal">
        <div class="hris-modal-header">
          <h2 class="hris-modal-title">Sanction Details</h2>
          <q-btn icon="fas fa-times" flat round dense v-close-popup class="hris-q-ghost" />
        </div>

        <div class="hris-modal-body" v-if="selectedSanction">
          <div class="row q-mb-sm">
            <div class="col-4 text-weight-bold">Type:</div>
            <div class="col-8">{{ selectedSanction.sanction_type }}</div>
          </div>
          <div class="row q-mb-sm">
            <div class="col-4 text-weight-bold">Incident Date:</div>
            <div class="col-8">
              {{ formatDate(selectedSanction.incident_date) }}
            </div>
          </div>
          <div class="row q-mb-sm">
            <div class="col-4 text-weight-bold">Issued Date:</div>
            <div class="col-8">
              {{ formatDate(selectedSanction.issued_date) }}
            </div>
          </div>
          <div class="row q-mb-sm" v-if="selectedSanction.effective_date">
            <div class="col-4 text-weight-bold">Effective Date:</div>
            <div class="col-8">
              {{ formatDate(selectedSanction.effective_date) }}
            </div>
          </div>
          <div class="row q-mb-sm" v-if="selectedSanction.end_date">
            <div class="col-4 text-weight-bold">End Date:</div>
            <div class="col-8">{{ formatDate(selectedSanction.end_date) }}</div>
          </div>
          <div class="row q-mb-sm">
            <div class="col-4 text-weight-bold">Status:</div>
            <div class="col-8">
              <q-chip
                :color="selectedSanction.status === 'active' ? 'green' : 'red'"
                text-color="white"
                dense
              >
                {{ selectedSanction.status }}
              </q-chip>
            </div>
          </div>
          <div class="row q-mb-sm">
            <div class="col-4 text-weight-bold">Reason:</div>
            <div class="col-8">{{ selectedSanction.reason }}</div>
          </div>
          <div class="row q-mb-sm" v-if="selectedSanction.status === 'revoked'">
            <div class="col-4 text-weight-bold">Revoked Reason:</div>
            <div class="col-8">{{ selectedSanction.revoked_reason }}</div>
          </div>
          <div class="row q-mb-sm">
            <div class="col-4 text-weight-bold">Issued By:</div>
            <div class="col-8">
              {{
                selectedSanction.issuer
                  ? selectedSanction.issuer.firstname +
                    " " +
                    selectedSanction.issuer.lastname
                  : "N/A"
              }}
            </div>
          </div>
        </div>
      </div>
    </q-dialog>

    <!-- Create / Edit Incident Report -->
    <q-dialog v-model="incidentDialog" persistent>
      <div class="hris-modal">
        <div class="hris-modal-header">
          <h2 class="hris-modal-title">
            {{
              isIncidentEdit ? "Edit Incident Report" : "New Incident Report"
            }}
          </h2>
          <q-btn icon="fas fa-times" flat round dense v-close-popup class="hris-q-ghost" />
        </div>
        <div class="hris-modal-body">
          <q-form @submit="saveIncidentReport" class="q-gutter-md">
            <q-select
              v-if="!isIncidentEdit"
              v-model="incidentReportedEmployee"
              use-input
              input-debounce="300"
              label="Employee reported (company ID)"
              :options="incidentEmployeeOptions"
              @filter="filterIncidentEmployees"
              option-label="name"
              option-value="employee_id"
              emit-value
              map-options
              outlined
              dense
              color="green"
              :rules="[(val) => !!val || 'Employee is required']"
            >
              <template v-slot:no-option>
                <q-item>
                  <q-item-section class="text-grey">No results</q-item-section>
                </q-item>
              </template>
            </q-select>
            <q-input
              v-else
              label="Employee reported"
              :value="incidentEditEmployeeName"
              outlined
              dense
              color="green"
              readonly
            />
            <q-input
              v-model="incidentForm.incident_date"
              label="Incident date"
              outlined
              dense
              color="green"
              mask="date"
              :rules="['date']"
            >
              <template v-slot:append>
                <i class="far fa-calendar-alt cursor-pointer">
                  <q-popup-proxy
                    ref="qDateIncident"
                    transition-show="scale"
                    transition-hide="scale"
                  >
                    <q-date
                      v-model="incidentForm.incident_date"
                      @input="() => $refs.qDateIncident.hide()"
                    />
                  </q-popup-proxy>
                </i>
              </template>
            </q-input>
            <q-input
              v-model="incidentForm.reason"
              label="Reason / report"
              type="textarea"
              outlined
              dense
              color="green"
              :rules="[(val) => !!val || 'Reason is required']"
            />
            <div class="hris-form-actions" style="margin-top: 8px; padding-top: 16px;">
              <q-btn label="Cancel" unelevated class="hris-q-ghost" v-close-popup />
              <q-btn
                :label="isIncidentEdit ? 'Update' : 'Save'"
                type="submit"
                unelevated
                class="hris-q-primary"
                :loading="incidentSaving"
              />
            </div>
          </q-form>
        </div>
      </div>
    </q-dialog>

    <!-- Incident details -->
    <q-dialog v-model="incidentDetailsDialog">
      <div class="hris-modal">
        <div class="hris-modal-header">
          <h2 class="hris-modal-title">Incident report details</h2>
          <q-btn icon="fas fa-times" flat round dense v-close-popup class="hris-q-ghost" />
        </div>
        <div class="hris-modal-body" v-if="selectedIncident">
          <div class="row q-mb-sm">
            <div class="col-4 text-weight-bold">Employee ID:</div>
            <div class="col-8">
              {{ selectedIncident.reported_employee_id }}
            </div>
          </div>
          <div class="row q-mb-sm" v-if="selectedIncident.employee">
            <div class="col-4 text-weight-bold">Name:</div>
            <div class="col-8">
              {{ selectedIncident.employee.firstname }}
              {{ selectedIncident.employee.lastname }}
            </div>
          </div>
          <div class="row q-mb-sm">
            <div class="col-4 text-weight-bold">Incident date:</div>
            <div class="col-8">
              {{ formatDate(selectedIncident.incident_date) }}
            </div>
          </div>
          <div class="row q-mb-sm">
            <div class="col-4 text-weight-bold">Reason / report:</div>
            <div class="col-8" style="white-space: pre-wrap">
              {{ selectedIncident.reason }}
            </div>
          </div>
          <div class="row q-mb-sm">
            <div class="col-4 text-weight-bold">Status:</div>
            <div class="col-8">
              <q-chip
                :color="getIncidentStatusColor(selectedIncident.status)"
                text-color="white"
                dense
              >
                {{ selectedIncident.status }}
              </q-chip>
            </div>
          </div>
          <div class="row q-mb-sm">
            <div class="col-4 text-weight-bold">Issued by:</div>
            <div class="col-8">
              {{
                selectedIncident.issuer
                  ? selectedIncident.issuer.firstname +
                    " " +
                    selectedIncident.issuer.lastname
                  : "N/A"
              }}
            </div>
          </div>
          <div
            class="row q-mb-sm"
            v-if="selectedIncident.status === 'resolved'"
          >
            <div class="col-4 text-weight-bold">Resolution notes:</div>
            <div class="col-8" style="white-space: pre-wrap">
              {{ selectedIncident.resolved_reason }}
            </div>
          </div>
        </div>
      </div>
    </q-dialog>

    <!-- Resolve incident -->
    <q-dialog v-model="incidentResolveDialog" persistent>
      <div class="hris-modal">
        <div class="hris-modal-header">
          <h2 class="hris-modal-title">Mark incident resolved</h2>
          <q-btn icon="fas fa-times" flat round dense v-close-popup class="hris-q-ghost" />
        </div>
        <div class="hris-modal-body">
          <q-form @submit="resolveIncidentReport" class="q-gutter-md">
            <q-input
              v-model="incidentResolvedReason"
              label="Resolution notes"
              type="textarea"
              outlined
              dense
              color="green"
              :rules="[(val) => !!val || 'Resolution notes are required']"
            />
            <div class="hris-form-actions" style="margin-top: 8px; padding-top: 16px;">
              <q-btn label="Cancel" unelevated class="hris-q-ghost" v-close-popup />
              <q-btn
                label="Confirm resolved"
                type="submit"
                unelevated
                class="hris-q-primary"
                :loading="incidentResolving"
              />
            </div>
          </q-form>
        </div>
      </div>
    </q-dialog>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { date } from "quasar";
import mixins from "../../mixins";

export default {
  mixins: [mixins],
  data() {
    return {
      mainTab: "incidents",
      sanctions: [],
      loading: false,
      filter: "",
      columns: [
        {
          name: "sanction_type",
          align: "center",
          label: "Sanction Type",
          field: "sanction_type",
          sortable: true,
        },
        {
          name: "offense",
          align: "left",
          label: "Offense",
          field: (row) => (row.offense ? row.offense.name : "N/A"),
          sortable: true,
        },
        {
          name: "incident_date",
          align: "center",
          label: "Incident Date",
          field: "incident_date",
          sortable: true,
        },
        {
          name: "issued_date",
          align: "center",
          label: "Issued Date",
          field: "issued_date",
          sortable: true,
        },
        {
          name: "effective_date",
          align: "center",
          label: "Effective Date",
          field: "effective_date",
          sortable: true,
        },
        {
          name: "end_date",
          align: "center",
          label: "End Date",
          field: "end_date",
          sortable: true,
        },
        {
          name: "status",
          align: "center",
          label: "Status",
          field: "status",
          sortable: true,
        },
        {
          name: "issued_by",
          align: "left",
          label: "Issued By",
          field: (row) =>
            row.issuer ? row.issuer.firstname + " " + row.issuer.lastname : "",
          sortable: true,
        },
        { name: "actions", align: "center", label: "Actions" },
      ],
      selectedSanction: null,
      detailsDialog: false,

      incidentReports: [],
      incidentLoading: false,
      incidentFilter: "",
      incidentStatusFilter: "active",
      incidentStatusOptions: [
        { label: "All", value: "all" },
        { label: "Active", value: "active" },
        { label: "Resolved", value: "resolved" },
      ],
      incidentColumns: [
        {
          name: "employee_reported",
          align: "left",
          label: "Employee reported",
          field: (row) => {
            const name = row.employee
              ? row.employee.firstname + " " + row.employee.lastname
              : "";
            const code = row.reported_employee_id || "";
            return (name + " " + code).trim();
          },
          sortable: true,
        },
        {
          name: "incident_date",
          align: "center",
          label: "Incident date",
          field: "incident_date",
          sortable: true,
        },
        {
          name: "reason",
          align: "left",
          label: "Reason / report",
          field: "reason",
          sortable: true,
        },
        {
          name: "status",
          align: "center",
          label: "Status",
          field: "status",
          sortable: true,
        },
        {
          name: "issued_by",
          align: "left",
          label: "Issued by",
          field: (row) =>
            row.issuer ? row.issuer.firstname + " " + row.issuer.lastname : "",
          sortable: true,
        },
        { name: "actions", align: "center", label: "Actions" },
      ],
      incidentDialog: false,
      isIncidentEdit: false,
      incidentSaving: false,
      incidentReportedEmployee: null,
      incidentEmployeeOptions: [],
      incidentEditEmployeeName: "",
      incidentForm: {
        id: null,
        incident_date: "",
        reason: "",
      },
      selectedIncident: null,
      incidentDetailsDialog: false,
      incidentResolveDialog: false,
      incidentResolving: false,
      incidentResolveId: null,
      incidentResolvedReason: "",

      ntes: [],
      nteLoading: false,
      nteFilter: "",
      nteColumns: [
        {
          name: "incident_date",
          align: "center",
          label: "Incident Date",
          field: (row) => (row.incident_report ? row.incident_report.incident_date : "N/A"),
          sortable: true,
        },
        {
          name: "deadline",
          align: "center",
          label: "Deadline",
          field: "deadline",
          sortable: true,
        },
        {
          name: "status",
          align: "center",
          label: "Status",
          field: "status",
          sortable: true,
        },
        {
          name: "issued_by",
          align: "left",
          label: "Issued By",
          field: (row) =>
            row.issuer ? row.issuer.firstname + " " + row.issuer.lastname : "",
          sortable: true,
        },
        { name: "actions", align: "center", label: "Actions" },
      ],
      explanationDialog: false,
      explanationForm: {
        id: null,
        explanation: "",
      },
      explanationSaving: false,
      selectedNte: null,
      nteDetailsDialog: false,
    };
  },
  computed: {
    token() {
      return this.$store.getters["auth/getToken"];
    },
    userId() {
      return this.$store.getters["auth/getUser"].id;
    },
  },
  mounted() {
    this.getMySanctions();
    this.getIncidentReports();
    this.getMyNtes();
  },
  methods: {
    formatDate(d) {
      if (!d) return "N/A";
      return date.formatDate(d, "MMMM DD, YYYY");
    },
    onTabChange(val) {
      if (val === "sanctions") this.getMySanctions();
      if (val === "incidents") this.getIncidentReports();
      if (val === "ntes") this.getMyNtes();
    },
    getMyNtes() {
      this.nteLoading = true;
      this.ntes = [];
      axios
        .create({
          headers: { Authorization: `Bearer ${this.token}` },
        })
        .get(this.apiUrl + "/ntes/personal")
        .then((res) => {
          this.ntes = res.data.data || [];
        })
        .finally(() => {
          this.nteLoading = false;
        });
    },
    getNteStatusColor(status) {
      switch (status) {
        case "pending":
          return "orange";
        case "submitted":
          return "blue";
        case "resolved":
          return "green";
        case "sanctioned":
          return "red";
        default:
          return "grey";
      }
    },
    viewNteDetails(row) {
      this.selectedNte = row;
      this.nteDetailsDialog = true;
    },
    openExplanationDialog(row) {
      this.explanationForm = {
        id: row.id,
        explanation: row.explanation || "",
      };
      this.explanationDialog = true;
    },
    submitExplanation() {
      this.explanationSaving = true;
      axios
        .create({
          baseURL: this.apiUrl,
          headers: { Authorization: `Bearer ${this.token}` },
        })
        .post("/nte/submit-explanation", this.explanationForm)
        .then((res) => {
          if (res.data.status) {
            this.$q.notify({
              color: "positive",
              message: "Explanation submitted successfully",
              icon: "fas fa-check",
            });
            this.explanationDialog = false;
            this.getMyNtes();
          }
        })
        .finally(() => {
          this.explanationSaving = false;
        });
    },
    getMySanctions() {
      this.loading = true;
      this.sanctions = [];
      const url = this.apiUrl + "/sanctions/employee/" + this.userId;

      axios
        .create({
          headers: {
            Authorization: `Bearer ${this.token}`,
          },
        })
        .get(url)
        .then((res) => {
          this.sanctions = res.data.data;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    getIncidentReports() {
      this.incidentLoading = true;
      this.incidentReports = [];
      let url = this.apiUrl + "/incident-reports";
      if (this.incidentStatusFilter && this.incidentStatusFilter !== "all") {
        url += "?status=" + this.incidentStatusFilter;
      }
      axios
        .create({
          headers: { Authorization: `Bearer ${this.token}` },
        })
        .get(url)
        .then((res) => {
          this.incidentReports = res.data.data || [];
        })
        .finally(() => {
          this.incidentLoading = false;
        });
    },
    getIncidentStatusColor(status) {
      if (status === "active") return "green";
      if (status === "resolved") return "blue-grey";
      return "grey";
    },
    filterIncidentEmployees(val, update) {
      if (val === "") {
        axios
          .create({
            headers: { Authorization: `Bearer ${this.token}` },
          })
          .get(this.apiUrl + "/employees/search?keyword=")
          .then((res) => {
            update(() => {
              this.incidentEmployeeOptions = res.data.data;
            });
          });
        return;
      }
      axios
        .create({
          headers: { Authorization: `Bearer ${this.token}` },
        })
        .get(this.apiUrl + "/employees/search?keyword=" + val)
        .then((res) => {
          update(() => {
            this.incidentEmployeeOptions = res.data.data;
          });
        })
        .catch((err) => {
          console.error("Error searching employees:", err);
        });
    },
    openIncidentDialog() {
      this.isIncidentEdit = false;
      this.incidentReportedEmployee = null;
      const today = new Date().toISOString().split("T")[0].replace(/-/g, "/");
      this.incidentForm = {
        id: null,
        incident_date: today,
        reason: "",
      };
      this.incidentDialog = true;
    },
    saveIncidentReport() {
      this.incidentSaving = true;
      const fd = new FormData();
      fd.append(
        "incident_date",
        this.incidentForm.incident_date.replace(/\//g, "-")
      );
      fd.append("reason", this.incidentForm.reason || "");
      let path = "/incident-report/create";
      if (this.isIncidentEdit) {
        path = "/incident-report/update";
        fd.append("id", String(this.incidentForm.id));
      } else {
        fd.append("employee_id", String(this.incidentReportedEmployee || ""));
      }
      axios
        .create({
          baseURL: this.apiUrl,
          headers: { Authorization: `Bearer ${this.token}` },
        })
        .post(path, fd)
        .then((res) => {
          if (res.data.status) {
            this.$q.notify({
              color: "positive",
              message: res.data.message || "Incident report saved",
              icon: "fas fa-check",
            });
            this.incidentDialog = false;
            this.getIncidentReports();
          } else {
            this.$q.notify({
              color: "negative",
              message: res.data.message || "Failed to save",
              icon: "fas fa-exclamation-triangle",
            });
          }
        })
        .catch((err) => {
          const msg =
            (err.response && err.response.data && err.response.data.message) ||
            err.message ||
            "Request failed";
          this.$q.notify({
            color: "negative",
            message: msg,
            icon: "fas fa-exclamation-triangle",
          });
        })
        .finally(() => {
          this.incidentSaving = false;
        });
    },
    editIncidentReport(row) {
      this.isIncidentEdit = true;
      this.incidentEditEmployeeName = row.employee
        ? row.employee.firstname + " " + row.employee.lastname
        : row.reported_employee_id || "N/A";
      this.incidentForm = {
        id: row.id,
        incident_date: row.incident_date
          ? row.incident_date.replace(/-/g, "/")
          : "",
        reason: row.reason || "",
      };
      this.incidentDialog = true;
    },
    viewDetails(row) {
      this.selectedSanction = row;
      this.detailsDialog = true;
    },
    viewIncidentDetails(row) {
      this.selectedIncident = row;
      this.incidentDetailsDialog = true;
    },
    confirmResolveIncident(row) {
      this.incidentResolveId = row.id;
      this.incidentResolvedReason = "";
      this.incidentResolveDialog = true;
    },
    resolveIncidentReport() {
      this.incidentResolving = true;
      const fd = new FormData();
      fd.append("id", String(this.incidentResolveId));
      fd.append("resolved_reason", this.incidentResolvedReason || "");
      axios
        .create({
          baseURL: this.apiUrl,
          headers: { Authorization: `Bearer ${this.token}` },
        })
        .post("/incident-report/resolve", fd)
        .then((res) => {
          if (res.data.status) {
            this.$q.notify({
              color: "positive",
              message: res.data.message || "Marked resolved",
              icon: "fas fa-check",
            });
            this.incidentResolveDialog = false;
            this.getIncidentReports();
          } else {
            this.$q.notify({
              color: "negative",
              message: res.data.message || "Failed to resolve",
              icon: "fas fa-exclamation-triangle",
            });
          }
        })
        .catch((err) => {
          const msg =
            (err.response && err.response.data && err.response.data.message) ||
            err.message ||
            "Request failed";
          this.$q.notify({
            color: "negative",
            message: msg,
            icon: "fas fa-exclamation-triangle",
          });
        })
        .finally(() => {
          this.incidentResolving = false;
        });
    },
    getSanctionColor(type) {
      switch (type) {
        case "Verbal Warning":
          return "blue-grey";
        case "Written Warning":
          return "orange";
        case "Suspension Without Pay":
          return "red-8";
        case "Demotion":
          return "red-10";
        case "Dismissal":
          return "black";
        default:
          return "grey";
      }
    },
  },
};
</script>

<style scoped>
.hris-sanctions-tabs {
  padding: 0 8px;
}

.hris-sanctions-tabs >>> .q-tab {
  text-transform: none;
  font-weight: 500;
}

.hris-sanctions-tabs >>> .q-tab--active {
  color: var(--hris-accent);
}
</style>
